import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Search, Swords } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { MatchCard } from "@/components/competition/MatchCard";
import { Pill, STRUCTURES } from "@/components/competition/shared";

export function TournamentCard({ t }) {
  return (
    <Link to={`/t/${t.tournament_id}`} className="panel group block p-4 transition-colors duration-200 hover:border-amber-500/60" data-testid={`tournament-card-${t.tournament_id}`}>
      <div className="flex items-start justify-between gap-2"><h3 className="font-heading text-lg text-amber-100 group-hover:text-aether-300">{t.name}</h3><Pill value={t.status} /></div>
      <p className="mt-1 font-mono text-[11px] uppercase tracking-wider text-copper-300">{t.community_name}</p>
      <p className="mt-2 text-sm text-slate-400"><Swords className="mr-1 inline h-4 w-4" />{STRUCTURES[t.structure]} · {t.structure === "pods" ? `pods of ${t.pod_size}` : `best of ${t.best_of}`} · cap {t.capacity}</p>
      {t.starts_at && <p className="mt-1 font-mono text-xs text-slate-500">{t.starts_at.slice(0, 16).replace("T", " ")}</p>}
    </Link>
  );
}

export function MyCompetition() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const { data } = useQuery({ queryKey: ["me", "competition", user?.user_id], queryFn: () => api.get("/me/competition").then((r) => r.data) });
  if (!data || (!data.matches.length && !data.tournaments.length && !data.seasons.length)) return null;
  const refresh = () => qc.invalidateQueries({ queryKey: ["me", "competition"] });
  return (
    <section className="mt-8 grid gap-4 lg:grid-cols-[1fr_320px]" data-testid="my-competition">
      <div>
        <p className="eyebrow mb-3">Matches waiting on you</p>
        {data.matches.length ? <div className="grid gap-3 sm:grid-cols-2">{data.matches.map((m) => <div key={m.match_id}><Link to={m.link} className="mb-1 block font-mono text-[10px] uppercase text-copper-300 hover:text-aether-300">Open event</Link><MatchCard m={m} users={data.users} refresh={refresh} /></div>)}</div>
          : <p className="text-sm text-slate-500" data-testid="my-matches-empty">Nothing to report right now.</p>}
      </div>
      <div className="panel space-y-2 p-4">
        <p className="eyebrow">My events</p>
        {data.tournaments.map((e) => e.tournament && <Link key={e.tournament_id} to={`/t/${e.tournament_id}`} className="flex justify-between gap-2 text-sm hover:text-aether-300" data-testid={`my-tournament-${e.tournament_id}`}><span className="truncate">{e.tournament.name}</span><Pill value={e.status} /></Link>)}
        {data.seasons.map((s) => s.league && <Link key={s.season_id} to={`/l/${s.league.league_id}?season=${s.season_id}`} className="flex justify-between gap-2 text-sm hover:text-aether-300" data-testid={`my-season-${s.season_id}`}><span className="truncate">{s.league.name} · {s.season?.name}</span><Pill value={s.status} /></Link>)}
      </div>
    </section>
  );
}

export default function Tournaments() {
  const { user } = useAuth();
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [structure, setStructure] = useState("");
  const params = { q: q || undefined, status: status || undefined, structure: structure || undefined };
  const { data, isLoading } = useQuery({ queryKey: ["tournaments", params], queryFn: () => api.get("/tournaments", { params }).then((r) => r.data) });
  return (
    <div className="container-x py-12" data-testid="tournaments-page">
      <p className="eyebrow">Tournaments</p>
      <h1 className="font-display text-4xl text-amber-100 sm:text-5xl">The Proving Grounds</h1>
      <p className="mt-2 max-w-2xl text-sm text-slate-400">Public tournaments run by approved communities: Swiss, single and double elimination, round robin and multiplayer pods. Organizers create events from their community page.</p>
      {user && <MyCompetition />}
      <div className="mt-10 flex flex-wrap gap-3">
        <label className="relative min-w-[220px] flex-1"><Search className="absolute left-3 top-3 h-4 w-4 text-slate-500" />
          <input className="field pl-9" placeholder="Search tournaments" value={q} onChange={(e) => setQ(e.target.value)} data-testid="tournaments-search-input" /></label>
        <select className="field w-44" value={status} onChange={(e) => setStatus(e.target.value)} data-testid="tournaments-status-filter">
          <option value="">Any status</option>{["registration", "check_in", "in_progress", "completed"].map((s) => <option key={s} value={s}>{s.replace("_", " ")}</option>)}</select>
        <select className="field w-52" value={structure} onChange={(e) => setStructure(e.target.value)} data-testid="tournaments-structure-filter">
          <option value="">Any structure</option>{Object.entries(STRUCTURES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select>
      </div>
      <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="tournaments-directory">
        {isLoading ? <p className="text-slate-500">Loading…</p> : data?.items.length ? data.items.map((t) => <TournamentCard key={t.tournament_id} t={t} />)
          : <p className="text-slate-500" data-testid="tournaments-empty">No tournaments match yet.</p>}
      </div>
    </div>
  );
}
